import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { DocumentObjectService, EventType as DocumentEventType } from '@Core/models/proprietary/document/document-object.service';
import { DocumentObjectState } from '@Core/models/proprietary/document/document-object.state';
import { TaskObjectService } from '@Core/models/proprietary/task/task-object.service';
import { TaskObjectState } from '@Core/models/proprietary/task/task-object.state';
import { EventType as TaskEventType, TaskActionType } from '@Core/models/proprietary/task/task-object.types';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';
import { FileActionType } from '@Core/services/proprietary/document-file.types';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { cloneDeep } from 'src/ep-utils';
import { TaskActionComponent } from '../../task-controls/task-action/task-action.component';

@Component({
    standalone: false,
    selector: 'ep-document-file',
    templateUrl: './document-file.component.html'
})
export class DocumentFileComponent implements OnInit, OnDestroy {
    @Select(DocumentObjectState.document)
    document$: Observable<any>;
    documentSub: Subscription;
    document: any;

    @Select(DocumentObjectState.filings)
    filings$: Observable<any>;

    @Select(TaskObjectState.task)
    task$: Observable<any>;
    taskSub: Subscription;
    task: any;

    dsTasks: Datasource;

    dsTaskProcessSub: Subscription;
    dsTaskGetSub: Subscription;
    dsDocumentProcessSub: Subscription;

    model = {
        taskLinkType: 'NOTASK',
        taskObjectCode: 'Task',
        autoOpenTask: false,
        selectedTaskID: ''
    };

    objectList: any = [];

    taskActionOptions = {
        titleText: 'New Task',
        buttonText: 'Close',
        event: DocumentEventType.Create,
        editTaskInfo: true,
        editNote: false,
        editRoute: false,
        editProjectInfo: false
    };

    constructor(
        private ds: DataService,
        public documentOS: DocumentObjectService,
        public taskOS: TaskObjectService,
        private store: Store,
        public activeModal: NgbActiveModal,
        public app: AppService,
        public os: ObjectService,
        private modalService: NgbModal
    ) {
        //TODO:   Temporary for CCF Demo  -- Need to get the object list from a search or from some user setting
        this.objectList = this.os.getObjectList(true, ['Q', 'Y']);

        this.dsTasks = ds.addNewDatasource('Task');
        this.dsTasks.maxRows = 250;
        this.dsTasks.maxRowLimiter = true;
    }

    taskObjectSelected() {
        let objectconfig: any = this.os.getObjectConfig(this.model.taskObjectCode);
        let tempTask = cloneDeep(this.task);
        tempTask.Task_Object.Object_Code = this.model.taskObjectCode;
        if (objectconfig) {
            tempTask.Task_Desc = objectconfig.Object_Name;
        }
        this.taskOS.updateTask(tempTask);
    }

    trackingID(index: number, object: any) {
        return object.DocLink_ID;
    }

    ngOnInit() {
        this.documentSub = this.document$.subscribe((x) => {
            if (x) {
                this.document = x;
            }
        });
        this.taskSub = this.task$.subscribe((x) => {
            if (x) {
                this.task = x;
            }
        });
        this.taskOS.newTask({
            Task_Desc: 'Manual Task',
            Task_Object: { Object_Code: 'Task' }
        });
    }

    ngOnDestroy() {
        if (this.taskSub) {
            this.taskSub.unsubscribe();
        }
        if (this.documentSub) {
            this.documentSub.unsubscribe();
        }
        if (this.dsTaskProcessSub) {
            this.dsTaskProcessSub.unsubscribe();
        }
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        if (this.dsDocumentProcessSub) {
            this.dsDocumentProcessSub.unsubscribe();
        }
    }

    save() {
        this.process(FileActionType.SaveOnly);
    }

    saveAndStartTask() {
        this.model.autoOpenTask = true;
        this.process(FileActionType.StartTask);
    }

    saveAndDistribute() {
        this.process(FileActionType.Distribute);
    }

    saveAndRoute() {
        this.process(FileActionType.Route);
    }

    process(action: string) {
        let tranOptions: any = this.model.selectedTaskID ? { Task_ID: this.model.selectedTaskID } : {};
        this.documentOS.process(DocumentEventType.Update, tranOptions);
        this.dsDocumentProcessSub = this.app.dsDocumentProcess.status$.subscribe((x) => {
            this.documentProcessStatusUpdated(x, action);
        });
    }

    documentProcessStatusUpdated(status: DataSourceStatus, action: string) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsDocumentProcessSub.unsubscribe();
                this.postProcess(action);
                break;
            case DataSourceStatus.Error:
                this.dsDocumentProcessSub.unsubscribe();
                break;
        }
    }

    postProcess(action: string) {
        if (this.model.taskLinkType != 'NOTASK') {
            // ExistingTask already linked via DocumentProcess
            if (this.model.taskLinkType === 'NEWTASK') {
                this.app.dsTaskProcess.reset();
                this.app.dsTaskGet.reset();
                this.taskOS.process(TaskEventType.Update, {
                    Auto_File_Doc_ID: this.document.Doc_ID
                });

                //TODO: HACK! This should probably be a subscription to an observable in TaskObjectService 
                // rather than watching the datasource in AppService
                this.dsTaskProcessSub = this.app.dsTaskProcess.status$.subscribe((x) => {
                    this.taskProcessStatusUpdated(x);
                });
                this.dsTaskGetSub = this.app.dsTaskGet.status$.subscribe((x) => {
                    this.taskGetStatusUpdated(x, action);
                });
                //END HACK
            } else {
                //Selected Link to Existing Task
                this.postFiling(action);
            }
        } else {
            //TODO:  add processing for non-task create
            this.taskOS.notifyDocumentSaved();

            if (action == FileActionType.Route) {
                this.taskOS.newTask({
                    Task_Desc: 'Route Document: ' + this.document.Doc_Name,
                    Task_Object: {
                        Object_ID: this.document.Doc_ID,
                        Object_Code: 'Document'
                    },
                    Task_Required: 'N'
                });

                const modalRef = this.modalService.open(TaskActionComponent, { centered: true, backdrop: 'static' });
                modalRef.componentInstance.taskActionMode = TaskActionType.Route;
            }

            this.closeModal(action);
        }
    }

    cancel() {
        this.closeModal(FileActionType.Cancel);
    }

    //fired after update complete
    taskProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Error:
                this.dsTaskProcessSub.unsubscribe();
                this.dsTaskGetSub.unsubscribe(); //clear "get" subscription since it will never be called on error
                break;
        }
    }

    //fired after update reloads the new task
    taskGetStatusUpdated(status: DataSourceStatus, action: string) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.postFiling(action);
                break;
            case DataSourceStatus.Error:
                this.dsTaskProcessSub.unsubscribe();
                this.dsTaskGetSub.unsubscribe();
                break;
        }
    }

    postFiling(action: string) {
        if (this.dsTaskProcessSub) {
            this.dsTaskProcessSub.unsubscribe();
        }
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        let task = this.app.dsTaskGet.objectNode; //get the taskID from the reply -- task created

        switch (action) {
            case FileActionType.Distribute:
                break;
            case FileActionType.StartTask:
                this.taskOS.openObjectFromTask(task);
                break;
            case FileActionType.SaveOnly:
                // if not in a session, then load the new task into task header
                if (!this.taskOS.isProjectActive) {
                    let taskID = task.Task_ID;
                    if (taskID) {
                        this.taskOS.loadTask(taskID, false);
                    }
                }
                this.taskOS.suppressTaskHeader(false);
                break;
            case FileActionType.Route:
                this.taskOS.loadTask(task.Task_ID, false);

                const modalRef = this.modalService.open(TaskActionComponent, { centered: true, backdrop: 'static' });
                modalRef.componentInstance.taskActionMode = TaskActionType.Route;
                break;

            default:
                break;
        }

        this.taskOS.notifyDocumentSaved();
        this.closeModal(action);
    }

    closeModal(closeAction: any) {
        this.activeModal.close({
            action: closeAction,
            document: this.document
        });
    }

    taskCheckBoxClick(task: any, e: MouseEvent) {
        if (this.model.selectedTaskID === task.Task_ID) {
            e.preventDefault();
        } else {
            this.onTaskRowClicked(task);
        }
    }

    onTaskRowClicked(task: any) {
        this.taskOS.loadTask(task.Task_ID);
        this.model.selectedTaskID = task.Task_ID;
    }

    getExistingTasks() {
        if (this.model.taskLinkType === 'EXISTINGTASK') {
            let retrieveNow: boolean = false;
            let criteria: any = {
                View: 'Full',
                Task_Status: 'OPEN'
            };

            let filingReferance = this.store.selectSnapshot(DocumentObjectState.filingReferance);
            if (filingReferance.Contract && filingReferance.Contract.Contract_ID) {
                criteria.Contract_ID = filingReferance.Contract.Contract_ID;
                criteria.Contract_Scope = 'SET';
                retrieveNow = true;
            }
            if (filingReferance.Claim) {
                criteria.Claim_ID = filingReferance.Claim.Claim_ID;
                retrieveNow = true;
            }

            if (retrieveNow) {
                this.dsTasks.getCollection(criteria);
            }
        }
    }
}
